import { useState, useEffect, useCallback } from 'react';
import { AccessibilitySettings } from './types';

const STORAGE_KEY = 'feevho_accessibility_settings';

export const defaultSettings: AccessibilitySettings = {
  fontSize: 'normal',
  highContrast: false,
  invertColors: false,
  grayscale: false,
  highlightLinks: false,
  dyslexiaFont: false,
  increasedSpacing: false,
  hideImages: false,
  bigCursor: false,
  stopAnimations: false,
  readingMask: false,
};

export const useAccessibility = () => {
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse accessibility settings", e);
      }
    }
    setIsLoaded(true);
  }, []);

  const applySettings = useCallback(() => {
    const root = document.getElementById('root');
    const body = document.body;
    const html = document.documentElement;
    
    if (!root) return;

    // נקה קלאסים קודמים כדי למנוע כפילויות
    root.classList.remove(
      'a11y-high-contrast', 'a11y-invert', 'a11y-grayscale',
      'a11y-highlight-links', 'a11y-dyslexia-font', 'a11y-increased-spacing',
      'a11y-hide-images', 'a11y-stop-animations'
    );
    body.classList.remove('a11y-big-cursor');
    html.classList.remove('a11y-text-large', 'a11y-text-xlarge');

    // החלת הגדרות חדשות
    if (settings.highContrast) root.classList.add('a11y-high-contrast');
    if (settings.invertColors) root.classList.add('a11y-invert');
    if (settings.grayscale) root.classList.add('a11y-grayscale');
    if (settings.highlightLinks) root.classList.add('a11y-highlight-links');
    if (settings.dyslexiaFont) root.classList.add('a11y-dyslexia-font');
    if (settings.increasedSpacing) root.classList.add('a11y-increased-spacing');
    if (settings.hideImages) root.classList.add('a11y-hide-images');
    if (settings.stopAnimations) root.classList.add('a11y-stop-animations');
    
    if (settings.bigCursor) body.classList.add('a11y-big-cursor');
    
    if (settings.fontSize === 'large') html.classList.add('a11y-text-large');
    if (settings.fontSize === 'xlarge') html.classList.add('a11y-text-xlarge');

  }, [settings]);

  useEffect(() => {
    if (isLoaded) {
      applySettings();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    }
  }, [settings, isLoaded, applySettings]);

  const updateSetting = <K extends keyof AccessibilitySettings>(key: K, value: AccessibilitySettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => setSettings(defaultSettings);

  return { settings, updateSetting, resetSettings };
};